/**
 * URL Helper Functions
 * 
 * Hardcoded URL'leri dinamik hale getirmek için helper fonksiyonlar
 * PageUrls ve UrlPatternService ile entegre çalışır
 * 
 * NOT: Hardcoded fallback'ler kaldırıldı! UrlBuilder kullanılmalı.
 */

import type { PageUrlMap } from '@/types/blog';
import { UrlBuilder } from '@/lib/url-builder';
import { buildLocalizedLink, normalizePath, resolveUrlFromMap } from '@/lib/blog-transformers';
import { PAGE_URL_KEY_RESERVATION_BOOK_NOW } from '@/constants/page-url-keys';

function withLanguagePrefix(path: string, language: string, defaultLanguage: string): string {
  const normalized = normalizePath(path);
  if (!language || language === defaultLanguage) {
    return normalized;
  }
  if (normalized === '/') {
    return `/${language}`;
  }
  if (normalized === `/${language}` || normalized.startsWith(`/${language}/`)) {
    return normalized;
  }
  return `/${language}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
}

const TOUR_LISTING_URL_TYPES = ['private-tours', 'group-tours'] as const;

/** Autocomplete / getProductFilters API (ProductTypeField `product_type` = tour) */
export function resolveFilterApiProductType(listingOrType: string | undefined): string {
  const t = (listingOrType ?? '').trim().toLowerCase();
  if (t === 'private-tours' || t === 'group-tours') {
    return 'tour';
  }
  return (listingOrType ?? '').trim() || 'tour';
}

/** Ana sayfa arama sekmesi (Private / Group / Yacht) → URL product type */
export function resolveSearchTabListingType(
  tab?: { type?: string; slug?: string } | null,
): string | undefined {
  if (!tab) return undefined;
  const type = tab.type?.trim().toLowerCase();
  const slug = tab.slug?.trim().toLowerCase();
  if (type === 'group-tours' || slug === 'group-tours' || slug?.includes('group-tour')) {
    return 'group-tours';
  }
  if (type === 'private-tours' || slug === 'private-tours' || slug?.includes('private-tour')) {
    return 'private-tours';
  }
  if (type === 'yacht' || slug === 'yacht') return 'yacht';
  if (type === 'ship_tour' || slug === 'ship_tour') return 'ship_tour';
  if (type === 'mice' || slug === 'mice') return 'mice';
  if (type && type !== 'tour') return tab.type!.trim();
  return undefined;
}

/**
 * Autocomplete yönlendirmesi: yanlışlıkla private-tours denemesin (pageUrls her iki base’i de çözer).
 * Öncelik: API product_type (group-tours/private-tours) → aktif sekme → yedek.
 */
function productTypesToTryForNavigation(
  apiProductType: string | undefined,
  tabHint: string | undefined,
): string[] {
  const tabListing = tabHint ? resolveSearchTabListingType({ type: tabHint, slug: tabHint }) : undefined;
  const apiNorm = normalizeProductTypeKeyForUrl(apiProductType ?? '', '').toLowerCase();

  if (apiNorm === 'group-tours' || apiNorm === 'private-tours') {
    return [apiNorm];
  }
  if (apiNorm && apiNorm !== 'tour' && !TOUR_LISTING_URL_TYPES.includes(apiNorm as (typeof TOUR_LISTING_URL_TYPES)[number])) {
    return [normalizeProductTypeKeyForUrl(apiProductType!, apiNorm)];
  }

  if (tabListing === 'group-tours' || tabListing === 'private-tours') {
    return [tabListing];
  }
  if (tabHint) {
    const tabNorm = normalizeProductTypeKeyForUrl(tabHint, '').toLowerCase();
    if (tabNorm && tabNorm !== 'tour') {
      return [normalizeProductTypeKeyForUrl(tabHint, tabNorm)];
    }
  }

  if (apiProductType) {
    return [normalizeProductTypeKeyForUrl(apiProductType, 'private-tours')];
  }
  return ['private-tours'];
}

const LISTING_LANDING_SLUGS = new Set(['private-tours', 'group-tours', 'yacht', 'ship_tour', 'mice']);

/**
 * Ana arama / autocomplete: kategori → `{main_cat_slug}/{cat_slug}`
 * (örn. /private-tours/cappadocia-tours). Kök landing slug’da tek segment.
 */
export function buildCategoryUrlForNavigation(
  categorySlug: string,
  categoryProductType: string | undefined,
  tabHint: string | undefined,
  pageUrls: PageUrlMap | undefined,
  language: string,
  defaultLanguage: string,
): string {
  const typesToTry = productTypesToTryForNavigation(categoryProductType, tabHint);
  const slug = categorySlug.trim().replace(/^\/+|\/+$/g, '');
  const isLandingSlug = LISTING_LANDING_SLUGS.has(slug.toLowerCase());

  if (pageUrls && language) {
    const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
    for (const pt of typesToTry) {
      // /private-tours/private-tours 404 olmasın
      if (isLandingSlug && (slug === pt || slug.toLowerCase() === pt.toLowerCase())) {
        const landing = builder.buildUrl(pt, {});
        if (landing && landing !== '#') {
          return landing;
        }
      }
      const url = builder.buildCategoryUrl(pt, slug);
      if (url && url !== '#') {
        return url;
      }
      // pageUrls’de `{pt}-cat` yoksa (örn. group-tours-cat): base + slug
      const base = builder.buildUrl(pt, {});
      if (base && base !== '#') {
        return withLanguagePrefix(
          buildLocalizedLink(base, slug, defaultLanguage),
          language,
          defaultLanguage,
        );
      }
    }
  }

  const pt = typesToTry[0] ?? 'private-tours';
  if (isLandingSlug && slug.toLowerCase() === pt.toLowerCase()) {
    return withLanguagePrefix(`/${pt}`, language, defaultLanguage);
  }
  return withLanguagePrefix(
    buildLocalizedLink(`/${pt}`, slug, defaultLanguage),
    language,
    defaultLanguage,
  );
}

/**
 * Ana arama / autocomplete: ürün → `{main_cat_slug}/{product_slug}`
 * (örn. /private-tours/istanbul-tour-5-days).
 */
export function buildProductUrlForNavigation(
  productSlug: string,
  productType: string | undefined,
  tabHint: string | undefined,
  pageUrls: PageUrlMap | undefined,
  language: string,
  defaultLanguage: string,
): string {
  const typesToTry = productTypesToTryForNavigation(productType, tabHint);
  const slug = productSlug.trim().replace(/^\/+|\/+$/g, '');

  if (pageUrls && language) {
    const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
    for (const pt of typesToTry) {
      const url = builder.buildProductDetailUrl(pt, slug);
      if (url && url !== '#') {
        return url;
      }
      const base = builder.buildUrl(pt, {});
      if (base && base !== '#') {
        return withLanguagePrefix(
          buildLocalizedLink(base, slug, defaultLanguage),
          language,
          defaultLanguage,
        );
      }
    }
  }

  const pt = typesToTry[0] ?? 'private-tours';
  return withLanguagePrefix(
    buildLocalizedLink(`/${pt}`, slug, defaultLanguage),
    language,
    defaultLanguage,
  );
}

/**
 * API'den gelen product/category `type` değerini `{type}-cat` / `{type}-detail` url_patterns ile uyumlu hale getirir.
 * Örn. destinations → tour (yanlış şablon anahtarı üretmesin).
 */
export function normalizeProductTypeKeyForUrl(type: string | undefined, fallback = 'tour'): string {
  const raw = String(type ?? fallback).trim();
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  if (lower === 'destinations' || lower === 'destination') return 'tour';
  return raw;
}

/**
 * Sayfa template_key'ine göre kategori liste linki için ipucu (group-tours-cat vs private-tours-cat).
 * index/home: UrlPatternService’teki ana liste genelde private-tours; generic `tour` API type’ı için taban ipucu.
 * Blokta categoryUrlProductType (group-tours / private-tours) her zaman önceliklidir.
 */
export function deriveCategoryListingProductTypeFromTemplateKey(templateKey?: string): string | undefined {
  if (!templateKey) return undefined;
  if (templateKey === 'index' || templateKey === 'home') return 'private-tours';
  if (templateKey.startsWith('group-tours')) return 'group-tours';
  if (templateKey.startsWith('private-tours')) return 'private-tours';
  if (templateKey === 'tour' || templateKey.startsWith('tour-')) return 'tour';
  return undefined;
}

/**
 * Kategori kartı href'i için ürün tipi anahtarı: önce API'deki net type (private-tours / group-tours),
 * generic `tour` ise blok/sayfa ipucu, sonra fallback.
 */
export function resolveProductTypeKeyForCategoryUrl(
  categoryType: string | undefined,
  templateOrBlockHint: string | undefined,
  fallback = 'tour'
): string {
  const raw = categoryType?.trim();
  if (raw) {
    const n = normalizeProductTypeKeyForUrl(raw, fallback);
    const nl = n.toLowerCase();
    if (nl === 'private-tours' || nl === 'group-tours') return n;
    if (nl !== 'tour' && nl !== '') return n;
  }
  const hintRaw = templateOrBlockHint?.trim();
  const hint =
    hintRaw && hintRaw !== 'auto' ? hintRaw : undefined;
  if (hint) {
    return normalizeProductTypeKeyForUrl(hint, fallback);
  }
  if (raw && raw.toLowerCase() === 'tour' && fallback && fallback.toLowerCase() !== 'tour') {
    return normalizeProductTypeKeyForUrl(fallback, fallback);
  }
  if (!raw || raw.toLowerCase() === 'tour') {
    return 'private-tours';
  }
  return normalizeProductTypeKeyForUrl(raw, fallback);
}

/**
 * Product type için category URL'i oluşturur
 * 
 * @param productType Product type (tour, yacht, cruise, vb.)
 * @param categorySlug Category slug
 * @param pageUrls PageUrls map (context'ten gelir)
 * @param language Language code
 * @param defaultLanguage Default language code
 * @returns Category URL (örn: /private-tours/eastern-turkey-tours)
 */
export function buildCategoryUrl(
  productType: string,
  categorySlug: string,
  pageUrls?: PageUrlMap,
  language?: string,
  defaultLanguage: string = 'en'
): string {
  if (!pageUrls || !language) {
    if (process.env.NODE_ENV === 'development') {
      console.error('buildCategoryUrl: pageUrls and language are required');
    }
    return '#';
  }
  
  const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
  return builder.buildCategoryUrl(productType, categorySlug);
}

/**
 * Product type için base URL'i döndürür
 * 
 * @param productType Product type (tour, yacht, cruise, vb.)
 * @param pageUrls PageUrls map (context'ten gelir)
 * @param language Language code
 * @param defaultLanguage Default language code
 * @returns Base URL (örn: /private-tours)
 */
export function getProductTypeBaseUrl(
  productType: string,
  pageUrls?: PageUrlMap,
  language?: string,
  defaultLanguage: string = 'en'
): string {
  if (!pageUrls || !language) {
    if (process.env.NODE_ENV === 'development') {
      console.error('getProductTypeBaseUrl: pageUrls and language are required');
    }
    return '';
  }
  
  const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
  return builder.getProductTypeBaseUrl(productType);
}

/**
 * Product detail URL'i oluşturur
 * 
 * @param productType Product type (tour, yacht, cruise, vb.)
 * @param productSlug Product slug
 * @param pageUrls PageUrls map (context'ten gelir)
 * @param language Language code
 * @param defaultLanguage Default language code
 * @param _categorySlug Kullanılmaz (eski çağrılar için); detay URL'si backend'de yalnızca ürün slug'ıdır
 * @returns Product detail URL (örn: /group-tours/istanbul-classics)
 */
export function buildProductDetailUrl(
  productType: string,
  productSlug: string,
  pageUrls?: PageUrlMap,
  language?: string,
  defaultLanguage: string = 'en',
  _categorySlug?: string
): string {
  if (!pageUrls || !language) {
    if (process.env.NODE_ENV === 'development') {
      console.error('buildProductDetailUrl: pageUrls and language are required');
    }
    return '#';
  }
  
  const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
  return builder.buildProductDetailUrl(productType, productSlug, _categorySlug);
}

/**
 * Special/Design URL'leri için helper
 * Örnek: /design/tours/special -> /private-tours/special (veya doğru base URL)
 * 
 * @param productType Product type (tour, yacht, cruise, vb.)
 * @param specialPath Special path (special, featured, vb.)
 * @param pageUrls PageUrls map (context'ten gelir)
 * @param language Language code
 * @param defaultLanguage Default language code
 * @returns Special URL
 */
export function buildSpecialUrl(
  productType: string,
  specialPath: string,
  pageUrls?: PageUrlMap,
  language?: string,
  defaultLanguage: string = 'en'
): string {
  if (!pageUrls || !language) {
    if (process.env.NODE_ENV === 'development') {
      console.error('buildSpecialUrl: pageUrls and language are required');
    }
    return '#';
  }
  
  const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
  return builder.buildSpecialUrl(productType, specialPath);
}

/**
 * Book-now (Confirm Dates) sayfası URL'i — UrlPattern template_key: reservation-book-now
 */
export function resolveReservationBookNowUrl(
  pageUrls: PageUrlMap | undefined,
  language: string,
  defaultLanguage: string,
  fallback = '/book-now',
): string {
  return resolveUrlFromMap(
    pageUrls,
    PAGE_URL_KEY_RESERVATION_BOOK_NOW,
    language,
    defaultLanguage,
    fallback,
  );
}
